import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, Download, X, ArrowUpRight } from "lucide-react";

const PDF = "/docs/WearSpray-Skin-Guidance-Concept-Paper.pdf";

const TOPICS = [
  "Seven-domain skin profile",
  "Age & occupation as context",
  "Ethnicity is never diagnostic",
  "Safety, fairness & privacy doctrine",
];

export function ConceptPaper() {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.body.style.overflow = open ? "hidden" : "";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <section className="border-t border-zinc-800 bg-[#0a0a0a]" data-testid="home-concept-paper">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left — the pitch */}
          <div className="lg:col-span-7">
            <span className="font-mono text-xs tracking-[0.28em] uppercase text-accent block mb-6">
              Concept Paper · v0.1
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tighter leading-[1.05] text-white max-w-2xl">
              The framework behind{" "}
              <span className="text-accent">contextual skin intelligence.</span>
            </h2>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-6 max-w-xl">
              A responsible framework for personalized skin guidance, moving from
              static, demographic-based advice to an individual, context-aware and
              learning-driven system built around Sense, Understand, Prompt, Act and Learn.
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-7 max-w-xl">
              {TOPICS.map((t) => (
                <span key={t} className="flex items-center gap-2 font-mono text-[11px] tracking-[0.12em] uppercase text-zinc-500">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent" /> {t}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-4 mt-9">
              <button
                onClick={() => setOpen(true)}
                data-testid="concept-paper-open"
                className="group inline-flex items-center gap-2.5 bg-accent text-black font-mono text-xs tracking-[0.18em] uppercase px-6 py-3.5 rounded-full hover:opacity-90 transition-opacity"
              >
                <FileText size={16} /> Read the paper
              </button>
              <a
                href={PDF}
                download="WearSpray-Skin-Guidance-Concept-Paper.pdf"
                data-testid="concept-paper-download"
                className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.15em] uppercase text-accent hover:underline"
              >
                <Download size={14} /> Download PDF
              </a>
            </div>
          </div>

          {/* Right — clickable document card */}
          <div className="lg:col-span-5">
            <button
              onClick={() => setOpen(true)}
              data-testid="concept-paper-card"
              className="group relative w-full text-left"
              aria-label="Open the WearSpray concept paper"
            >
              <div
                className="absolute -inset-4 blur-3xl opacity-40 pointer-events-none transition-opacity group-hover:opacity-70"
                style={{ background: "radial-gradient(circle, rgba(0,229,255,0.18), transparent 65%)" }}
              />
              <div className="relative border border-zinc-800 bg-gradient-to-b from-[#111] to-[#0b0b0b] rounded-2xl p-8 transition-transform duration-300 group-hover:-translate-y-1">
                <div className="flex items-start justify-between">
                  <FileText size={30} className="text-accent" strokeWidth={1.6} />
                  <ArrowUpRight size={20} className="text-zinc-600 transition-colors group-hover:text-accent" />
                </div>
                <div className="mt-8 space-y-2.5">
                  <div className="h-2 rounded-full bg-zinc-700 w-3/4" />
                  <div className="h-2 rounded-full bg-zinc-800 w-full" />
                  <div className="h-2 rounded-full bg-zinc-800 w-5/6" />
                  <div className="h-2 rounded-full bg-zinc-800 w-11/12" />
                  <div className="h-2 rounded-full bg-zinc-800 w-2/3" />
                </div>
                <div className="mt-8 pt-6 border-t border-zinc-800 flex items-center justify-between">
                  <span className="font-heading text-lg text-white leading-tight">
                    Skin Guidance<br />Concept Paper
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-zinc-500">21 pp · PDF</span>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Modal viewer */}
      {mounted && createPortal(
        <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            data-testid="concept-paper-modal"
          >
            <div
              className="absolute inset-0 bg-black/85 backdrop-blur-sm"
              onClick={() => setOpen(false)}
            />
            <motion.div
              className="relative w-full max-w-5xl h-[90vh] bg-[#0d0d0d] border border-zinc-700 rounded-2xl overflow-hidden flex flex-col shadow-2xl"
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 16 }}
              transition={{ type: "spring", stiffness: 260, damping: 24 }}
            >
              <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-zinc-800 shrink-0">
                <div className="min-w-0">
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-accent block">Concept Paper · v0.1</span>
                  <h3 className="font-heading text-lg text-white truncate">Contextual Skin Intelligence</h3>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <a
                    href={PDF}
                    download="WearSpray-Skin-Guidance-Concept-Paper.pdf"
                    data-testid="concept-paper-modal-download"
                    className="inline-flex items-center gap-2 bg-accent text-black font-mono text-[11px] tracking-[0.14em] uppercase px-4 py-2.5 rounded-full hover:opacity-90 transition-opacity"
                  >
                    <Download size={14} /> Download
                  </a>
                  <button
                    onClick={() => setOpen(false)}
                    data-testid="concept-paper-close"
                    aria-label="Close"
                    className="w-9 h-9 rounded-full border border-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white hover:border-zinc-500 transition-colors"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>
              <iframe
                src={`${PDF}#view=FitH`}
                title="WearSpray Skin Guidance Concept Paper"
                className="flex-1 w-full bg-zinc-900"
                data-testid="concept-paper-iframe"
              />
            </motion.div>
          </motion.div>
        )}
        </AnimatePresence>,
        document.body
      )}
    </section>
  );
}
